import axios from 'axios'
import { baseUrl } from './util'
import { recordApiRequest, recordError, getCache, setCache } from './performance'
import { getCsrfToken, safeLog, decryptData, encryptData } from './security'
import { getStorage, setStorage, removeStorage } from './storage'

const DEFAULT_TIMEOUT = 30000
const DEFAULT_RETRY_COUNT = 2
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504]

const pendingRequests = new Map()
let isRedirectingToLogin = false

const requestInterceptor = (config) => {
  const token = getStorage('token', '')
  config.headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
    ...config.headers
  }
  if (token) {
    config.headers['token'] = token
  }
  
  return config
}

const responseInterceptor = (response) => {
  if (response.status === 401) {
    if (isRedirectingToLogin) {
      safeLog('正在跳转登录页，跳过重复处理')
      return Promise.reject(new Error('登录已过期'))
    }
    
    isRedirectingToLogin = true
    safeLog('检测到401错误，开始处理登录过期')
    
    removeStorage('token')
    removeStorage('userInfos')
    
    alert('登录已过期，请重新登录')
    
    setTimeout(() => {
      safeLog('执行跳转登录页')
      window.location.href = '#/login'
      isRedirectingToLogin = false
    }, 2000)
    
    return Promise.reject(new Error('登录已过期'))
  }
  
  if (response.data && (response.data.msg || response.data.message)) {
    const errorMsg = response.data.msg || response.data.message
    if (errorMsg.includes('Token') || errorMsg.includes('token') || errorMsg.includes('无效')) {
      if (isRedirectingToLogin) {
        safeLog('正在跳转登录页，跳过重复处理')
        return Promise.reject(new Error(errorMsg))
      }
      
      isRedirectingToLogin = true
      safeLog('检测到token无效错误，开始处理:', errorMsg)
      
      removeStorage('token')
      removeStorage('userInfos')
      
      alert('登录已过期，请重新登录')
      
      setTimeout(() => {
        safeLog('执行跳转登录页')
        window.location.href = '#/login'
        isRedirectingToLogin = false
      }, 2000)
      
      return Promise.reject(new Error(errorMsg))
    }
  }
  
  if (response.status >= 400 && response.data) {
    const errorMsg = response.data.message || response.data.msg || '请求失败'
    return {
      code: 0,
      msg: errorMsg,
      data: null
    }
  }
  
  let resultData = response.data
  
  if (resultData && resultData.data && typeof resultData.data === 'string') {
    safeLog('检测到加密数据，开始解密')
    try {
      const decryptedData = decryptData(resultData.data)
      resultData = { ...resultData, data: decryptedData }
      safeLog('解密成功')
    } catch (error) {
      safeLog('解密失败，使用原始数据:', error)
    }
  }
  
  return resultData
}

const isRetryable = (error, attempt) => {
  if (attempt >= DEFAULT_RETRY_COUNT) return false
  
  if (error.response && error.response.status && RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return true
  }
  
  if (!error.response) {
    return true
  }
  
  return false
}

const requestWithRetry = async (url, options = {}) => {
  let lastError = null
  
  for (let attempt = 0; attempt <= DEFAULT_RETRY_COUNT; attempt++) {
    try {
      const finalOptions = requestInterceptor({ ...options })
      const startTime = Date.now()
      const cacheKey = `${url}_${JSON.stringify(finalOptions.data || {})}`
      
      if (finalOptions.cache !== false) {
        const cachedData = getCache(cacheKey)
        if (cachedData) {
          safeLog(`使用缓存数据: ${url}`)
          return cachedData
        }
      }
      
      const response = await axios({
        url: `${baseUrl}${url}`,
        method: finalOptions.method || 'GET',
        data: finalOptions.data,
        params: finalOptions.method === 'GET' ? finalOptions.data : undefined,
        headers: finalOptions.headers,
        timeout: finalOptions.timeout || DEFAULT_TIMEOUT
      })
      
      const duration = Date.now() - startTime
      recordApiRequest(url, finalOptions.method, response.status, duration)
      
      const result = await responseInterceptor(response)
      
      if (finalOptions.cache !== false && result) {
        setCache(cacheKey, result)
      }
      
      return result
      
    } catch (error) {
      lastError = error
      
      if (error.response && error.response.status && error.response.data) {
        const errorData = error.response.data
        const errorMsg = errorData.message || errorData.msg || ''
        
        if (errorMsg.includes('Token') || errorMsg.includes('token') || errorMsg.includes('无效')) {
          if (!isRedirectingToLogin) {
            isRedirectingToLogin = true
            safeLog('检测到token无效错误，开始处理:', errorMsg)
            
            removeStorage('token')
            removeStorage('userInfos')
            
            alert('登录已过期，请重新登录')
            
            setTimeout(() => {
              safeLog('执行跳转登录页')
              window.location.href = '#/login'
              isRedirectingToLogin = false
            }, 2000)
          }
          return Promise.reject(new Error(errorMsg))
        }
      }
      
      if (attempt < DEFAULT_RETRY_COUNT && isRetryable(error, attempt)) {
        safeLog(`请求失败，正在重试 (${attempt + 1}/${DEFAULT_RETRY_COUNT}):`, error)
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
        continue
      }
      
      safeLog(`请求失败，已达到最大重试次数:`, error)
      return Promise.reject(error)
    }
  }
  
  return Promise.reject(lastError)
}

const request = (url, options = {}) => {
  return requestWithRetry(url, options)
}

export const loginAPI = (data) => request('/api/members/login', { 
  method: 'POST', 
  data: {
    phone: data.phone,
    datas: data.datas,
    timestamp: Date.now()
  },
  cache: false
})

export const refreshToken = async () => {
  try {
    const refreshTokenValue = getStorage('refreshToken', '')
    if (!refreshTokenValue) {
      throw new Error('无refreshToken')
    }
    
    const response = await axios({
      url: `${baseUrl}/auth/refresh`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'token': refreshTokenValue
      }
    })
    
    if (response.data.code === 1) {
      setStorage('token', response.data.data.token, { ttl: 7 * 24 * 60 * 60 * 1000 })
      return response.data.data.token
    }
    throw new Error('刷新失败')
  } catch (error) {
    safeLog('刷新token失败:', error)
    window.location.href = '#/login'
    throw error
  }
}

export const checkLoginStatus = () => {
  return new Promise((resolve, reject) => {
    const token = getStorage('token', '')
    const isLogin = token && token.length > 0
    
    if (!isLogin) {
      window.location.href = '#/login'
      reject(new Error('未登录'))
    } else {
      window.location.href = '#/index'
      resolve(true)
    }
  })
}

export const logoutAPI = () => {
  const token = getStorage('token', '')
  return request('/api/user/logout', { 
    method: 'POST',
    data: { token: token },
    cache: false 
  })
}

export const getUserInfo = () => {
  const token = getStorage('token', '')
  return request('/api/members/getinfo', { 
    method: 'POST', 
    data: { token: token },
    cache: false
  })
}

export const getLoan = () => {
  const token = getStorage('token', '')
  return request('/api/members/loan', { 
    method: 'POST', 
    data: { token: token }
  })
}

export const getHomePageData = () => {
  const token = getStorage('token', '')
  return request('/api/members/homepage', { 
    method: 'POST', 
    data: { token: token },
    cache: false
  })
}

export const updateUserInfo = (data) => request('/api/user/update', { 
  method: 'POST', 
  data,
  cache: false
})

export const verifyToken = (token) => request('/api/auth/verify', { 
  method: 'POST', 
  data: { token },
  cache: false
})

export default request
